// Pure helpers that produce the help-text strings shown under each control in
// `DatePeriodSection`. Help text lives on different controls per `periodKind`:
//   - 7 standard kinds → on the "Date period" select.
//   - TRAILING_MONTHS  → on the "Trailing months" number input.
//   - CUSTOM (param)   → on each leg's parameter select.
//   - CUSTOM (date)    → no help text.

import plur from "plur";
import type { DatePeriodKind } from "~/graphql/supergraphTypes";

const assertNever = (value: never): never => {
  throw new Error(`Unhandled DatePeriodKind: ${String(value)}`);
};

/** Help text for the "Date period" select. Returns `undefined` for kinds whose
 * help text lives on a child control (TRAILING_MONTHS, CUSTOM). */
export const periodSelectHelpText = (periodKind: DatePeriodKind): string | undefined => {
  switch (periodKind) {
    case "YEAR_TO_DATE":
      return "First day of publication year to publication date";
    case "QUARTER_TO_DATE":
      return "First day of publication quarter to publication date";
    case "MONTH_TO_DATE":
      return "First day of publication month to publication date";
    case "INCEPTION_TO_DATE":
      return "Inception date to publication date";
    case "BEGINNING_YEAR":
      return "Inception to first day of publication year";
    case "BEGINNING_QUARTER":
      return "Inception to first day of publication quarter";
    case "BEGINNING_MONTH":
      return "Inception to first day of publication month";
    case "TRAILING_MONTHS":
    case "CUSTOM":
      return undefined;
    default:
      return assertNever(periodKind);
  }
};

/** Help text rendered under the "Trailing months" number input. */
export const trailingMonthsHelpText = (months: number | null): string => {
  const n = months ?? 0;
  return `Trailing ${n} ${plur("month", n)} relative to the publication date`;
};

/** Generic help text shown under a CUSTOM date leg's parameter select. The
 * copy is the same regardless of which parameter is selected. */
export const CUSTOM_PARAMETER_HELP_TEXT = "Date will be defined when you run the publication";
